import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from '../../../contexts/UserContext';
import { getAllActivityData } from '../../../utils/APIs';

// Import the individual components
import ListUsers from '../../../components/FunctionDetails/UserManagements/ListUsers';
import ValidateSMTP from '../../../components/FunctionDetails/UserManagements/ValidateSMTP';
import ValidateName from '../../../components/FunctionDetails/UserManagements/ValidateName';
import ListActivity from '../../../components/FunctionDetails/ListActivity';
import ListOffers from '../../../components/FunctionDetails/ListOffers';
import ListAudiences from '../../../components/FunctionDetails/ListAudiences';
import ListMboxes from '../../../components/FunctionDetails/ListMboxes';
import ListProperties from '../../../components/FunctionDetails/ListProperties';
import ListEnvironments from '../../../components/FunctionDetails/ListEnvironments';
import ListEntities from '../../../components/FunctionDetails/ListEntities';
import ValidateActivityName from '../../../components/FunctionDetails/ValidateActivityName';
import AdobeTargetActivitiesRevisions from '../../../components/FunctionDetails/AdobeTargetActivitiesRevisions';
import CreateAtAudienceFromTealium from '../../../components/FunctionDetails/CreateAtAudienceFromTealium';
import backwardArrow from '../../../assets/icons/backward-arrow.svg';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import ManageAdobeTargetModelApi from '../../../components/FunctionDetails/ManageAdobeTargetModelApi';
import ManageRecommendation from '../../../components/FunctionDetails/ManageRecommendation';
import GithubExperienceSynchronization from '../../../components/FunctionDetails/GithubExperienceSynchronization';
import TealiumDataLayerRepository from '../../../components/FunctionDetails/TealiumDataLayerRepository';
import AdobeAnalyticsUsage from '../../../components/FunctionDetails/AdobeAnalyticsUsage';
import AdobeTargetUsage from '../../../components/FunctionDetails/AdobeTargetUsage';
import ProductFeeds from '../../../components/FunctionDetails/ProductFeeds/ProductFeeds';

const Report = () => {
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showBackNavigationArrow, setShowBackNavigationArrow] = useState(true);

    const productFeedsTaskId = content?.tasks?.product_feeds?.id || 'product_feeds';

    // Define available task IDs for different report types
    const availableTasks = {
        users: [content.tasks.users.id, content.tasks.smtp.id, content.tasks.validate_name.id, content.tasks.manage_recommendation.id, productFeedsTaskId],
        activity: [content.tasks.adobe_analytics_usage.id, content?.tasks?.adobe_target_usage?.id || 'adobe_target_usage', content.tasks.tealium_data_layer_repository.id, content.tasks.activities.id, content.tasks.offers.id, content.tasks.audiences.id, content.tasks.mboxes.id, content.tasks.properties.id, content.tasks.environments.id, content.tasks.entities.id, content.tasks.validate_activity_name.id, content.tasks.manage_activity_revisions.id, content.tasks.map_as_audience_to_at_audience.id, content.tasks.manage_adobe_target_model_api.id, content.tasks.github_experience_sync.id]
    };
    // Function to fetch activity data
    const getActivityData = async () => {
        const tenant_name = state?.tenant_name ? state.tenant_name : userData?.defaultAdobeIO?.tenant_name
        const type = id === content.tasks.manage_adobe_target_model_api.id ? content.tasks.activities.id : id
        try {
            setLoading(true);
            const result = await getAllActivityData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                tenant_name,
                type,
                undefined,
                userData?.defaultWorkspace?.workspace_id
            );
            setData(result.data);
            setError("");
        } catch (error) {
            console.log("error getAllIntegrationData: ", error);
            setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    // Navigate if task ID is invalid
    useEffect(() => {
        if (![...availableTasks.users, ...availableTasks.activity].includes(id)) {
            navigate("/functions");
        }
    }, [userData?.selectedOrganization]);

    useEffect(() => {
        if (availableTasks.activity.includes(id) && userData?.defaultAdobeIO) {
            setData(null);
            setError("");
            getActivityData();
        }
    }, [id, userData?.defaultAdobeIO, userData?.defaultWorkspace]);

    // Mapping between task IDs and components
    const taskComponentMap = {
        [content.tasks.users.id]: <ListUsers />,
        [content.tasks.smtp.id]: <ValidateSMTP />,
        [content.tasks.validate_name.id]: <ValidateName />,
        [content.tasks.activities.id]: <ListActivity loading={loading} data={data?.activities || []} metadata={data} error={error} />,
        [content.tasks.offers.id]: <ListOffers loading={loading} data={data?.offers || []} metadata={data} error={error} />,
        [content.tasks.audiences.id]: <ListAudiences loading={loading} data={data?.audiences || []} metadata={data} error={error} />,
        [content.tasks.mboxes.id]: <ListMboxes loading={loading} data={data?.mboxes || []} metadata={data} error={error} />,
        [content.tasks.properties.id]: <ListProperties loading={loading} data={data?.properties || []} metadata={data} error={error} />,
        [content.tasks.environments.id]: <ListEnvironments loading={loading} data={data?.environments || []} metadata={data} error={error} />,
        [content.tasks.manage_recommendation.id]: <ManageRecommendation />,
        [content.tasks.manage_adobe_target_model_api.id]: <ManageAdobeTargetModelApi
                                                            setShowBackNavigationArrow={setShowBackNavigationArrow}
                                                            loading={loading}
                                                            data={data?.activities?.filter(activity => activity.type === "abt") || []}
                                                            tenant_name={state?.tenant_name}
                                                            campaign_id={state?.campaign_id}
                                                        />,
        [content.tasks.entities.id]: <ListEntities />,
        [content.tasks.validate_activity_name.id]: <ValidateActivityName />,
        [content.tasks.manage_activity_revisions.id]: <AdobeTargetActivitiesRevisions />,
        [content.tasks.github_experience_sync.id]: <GithubExperienceSynchronization />,
        [content.tasks.map_as_audience_to_at_audience.id]: <CreateAtAudienceFromTealium />,
        [content.tasks.tealium_data_layer_repository.id]: <TealiumDataLayerRepository />,
        [content.tasks.adobe_analytics_usage.id]: <AdobeAnalyticsUsage setShowBackNavigationArrow={setShowBackNavigationArrow} />,
        [content?.tasks?.adobe_target_usage?.id || 'adobe_target_usage']: <AdobeTargetUsage setShowBackNavigationArrow={setShowBackNavigationArrow} />,
        [productFeedsTaskId]: <ProductFeeds setShowBackNavigationArrow={setShowBackNavigationArrow} />
    };

    return (
        <div className="w-full max-w-screen px-4 md:px-8 my-6">
            {showBackNavigationArrow && <div class="w-full pb-2">
                <a href="#" class="font-medium text-primary hover:underline flex items-center" onClick={(e) => {
                    e.preventDefault();
                    if (state?.viewMode) {
                        navigate("/functions", { state: { mode: state.viewMode } })
                    } else {
                        navigate("/functions/saved-tasks")
                    }
                }}>
                    <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' />
                    {
                        state?.viewMode ? content.tasks.back_cta_text : content.tasks.back_cta_text2
                    }
                </a>
            </div>}
            <main id="main" className="flex flex-col space-y-3 items-start flex-grow w-full">
                {taskComponentMap[id] || <p>{content.FUNCTIONS.invalid_task}</p>}
            </main>
        </div>
    );
};

export default Report;
